import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLanguage } from "../context/LanguageContext";
import { scrollToSection } from "../lib/scroll";

const HERO_IMAGE =
  "https://customer-assets-lxgj4vgw.emergentagent.net/job_azorunique-home/artifacts/qbbp2ubu_Geral%20-%20Frontal.jpg";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block will-change-transform"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);

  return (
    <section id="hero" ref={ref} className="relative flex min-h-screen items-end overflow-hidden" data-testid="hero-section">
      <motion.div className="absolute inset-0" style={{ y }}>
        <img
          src={HERO_IMAGE}
          alt="Arquitetura AZORUniqueHome nos Açores"
          className="h-[115%] w-full object-cover"
          data-testid="hero-image"
        />
      </motion.div>
      <div className="absolute inset-0 bg-basalt/25" />
      <div className="absolute inset-0 bg-gradient-to-t from-basalt/90 via-basalt/30 to-transparent" />

      <div className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-28 pt-44">
        <motion.p
          data-testid="hero-kicker"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.1 }}
          className="mb-6 font-mono text-[11px] uppercase tracking-[0.35em] text-terracotta drop-shadow-[0_1px_8px_rgba(13,17,23,0.9)]"
        >
          {t.hero.kicker}
        </motion.p>

        <h1
          data-testid="hero-title"
          className="font-display text-4xl font-medium leading-[1.08] tracking-tight text-sand drop-shadow-[0_2px_14px_rgba(13,17,23,0.9)] sm:text-5xl lg:text-6xl"
        >
          <MaskedLine delay={0.3}>{t.hero.line1}</MaskedLine>
          <MaskedLine delay={0.45}>
            <em className="italic text-sand">{t.hero.line2}</em>
          </MaskedLine>
        </h1>

        <motion.p
          data-testid="hero-subtitle"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.75, ease: [0.22, 1, 0.36, 1] }}
          className="mt-7 max-w-2xl text-sm leading-relaxed text-sand/80 drop-shadow-[0_1px_8px_rgba(13,17,23,0.9)] sm:text-base"
        >
          {t.hero.subtitle}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-cta-projects"
            onClick={() => scrollToSection("#projetos")}
            className="rounded-full bg-terracotta px-7 py-3.5 text-sm font-medium text-basalt transition-colors duration-300 hover:bg-sand"
          >
            {t.hero.ctaPrimary}
          </button>
          <button
            data-testid="hero-cta-about"
            onClick={() => scrollToSection("#sobre-nos")}
            className="rounded-full border border-sand/30 px-7 py-3.5 text-sm font-medium text-sand transition-colors duration-300 hover:border-terracotta hover:text-terracotta"
          >
            {t.hero.ctaSecondary}
          </button>
        </motion.div>
      </div>

      <motion.button
        data-testid="hero-scroll-cue"
        onClick={() => scrollToSection("#abordagem")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute bottom-8 right-6 z-10 hidden flex-col items-center gap-3 sm:flex"
        aria-label={t.hero.scroll}
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-sand/50 [writing-mode:vertical-rl]">
          {t.hero.scroll}
        </span>
        <motion.span
          className="block h-12 w-px bg-terracotta/70"
          animate={{ scaleY: [0, 1, 1, 0], transformOrigin: ["top", "top", "bottom", "bottom"] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
        />
      </motion.button>
    </section>
  );
};
